import { useEffect, useState, type ReactNode } from 'react';
import { Button, Card, Field, Input, Select, Toolbar } from '@biovet/ui';
import {
  allCurrencies,
  baseCurrency,
  companyToday,
  monthOf,
  rateFor,
  shiftMonth,
} from '../lib/reference-data.js';


export function Fact({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div>
      <div className="bb-eyebrow pb-1.5">{label}</div>
      <div className="text-[13px] font-medium text-[var(--ink)]">{value}</div>
    </div>
  );
}

export function FactGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 xl:grid-cols-6">{children}</div>
  );
}

export function Th({
  children,
  align = 'left',
  width,
}: {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string;
}) {
  return (
    <th
      style={width ? { width } : undefined}
      className={`bb-eyebrow border-b border-[var(--line-2)] px-2.5 py-2 font-medium first:px-[var(--pad)] last:px-[var(--pad)] ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

export function CurrencyRateFields({
  currency,
  fxRate,
  onChange,
  size = 'sm',
  label = 'Currency',
  rateLabel = 'Rate',
  rateHint,
  disabled = false,
}: {
  currency: string;
  fxRate: string;
  onChange: (next: { currency: string; fxRate: string }) => void;
  size?: 'sm' | 'md';
  label?: string;
  rateLabel?: string;
  rateHint?: string;
  disabled?: boolean;
}) {
  const base = baseCurrency();
  const options = allCurrencies();
  const onBase = currency === base;

  const list =
    options.length > 0
      ? options
      : [{ code: currency || base, name: '', isBase: currency === base, rate: null }];

  return (
    <>
      <Field label={label}>
        <Select
          size={size}
          value={currency}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            onChange({ currency: next, fxRate: rateFor(next) ?? '' });
          }}
        >
          {list.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={rateLabel}
        hint={
          rateHint ??
          (onBase
            ? undefined
            : rateFor(currency) === null
              ? `No ${currency}/${base} rate on file`
              : 'base = txn × rate')
        }
      >
        <Input
          size={size}
          numeric
          value={fxRate}
          disabled={disabled || onBase}
          onChange={(e) => onChange({ currency, fxRate: e.target.value })}
        />
      </Field>
    </>
  );
}

export function DateWindow({
  range,
  onChange,
  label = 'Whole book',
}: {
  range: { from: string; to: string };
  onChange: (next: { from: string; to: string }) => void;
  label?: string;
}) {
  const anchor = range.from || companyToday();
  return (
    <>
      <Field label="From">
        <Input
          size="sm"
          type="date"
          value={range.from}
          aria-label="From date"
          onChange={(e) => onChange({ ...range, from: e.target.value })}
        />
      </Field>
      <Field label="To">
        <Input
          size="sm"
          type="date"
          value={range.to}
          aria-label="To date"
          onChange={(e) => onChange({ ...range, to: e.target.value })}
        />
      </Field>
      <Button
        size="sm"
        variant="ghost"
        title="Previous month"
        onClick={() => onChange(shiftMonth(anchor, -1))}
      >
        ‹
      </Button>
      <Button
        size="sm"
        variant="ghost"
        title="Next month"
        onClick={() => onChange(shiftMonth(anchor, 1))}
      >
        ›
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onChange(monthOf(companyToday()))}>
        This month
      </Button>
      {(range.from || range.to) && (
        <Button size="sm" variant="ghost" onClick={() => onChange({ from: '', to: '' })}>
          {label}
        </Button>
      )}
    </>
  );
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function RegisterWindow({
  range,
  onChange,
  count,
  noun,
}: {
  range: { from: string; to: string };
  onChange: (next: { from: string; to: string }) => void;
  count: number;
  noun: string;
}) {
  const windowed = range.from || range.to;
  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <Toolbar>
        <DateWindow range={range} onChange={onChange} label="All time" />
        <div className="flex-1" />
        <span className="text-[12px] text-[var(--ink-3)]">
          {count} {noun}
          {count === 1 ? '' : 's'}{' '}
          {windowed
            ? `dated ${range.from || 'the start of the book'} to ${range.to || 'today'}`
            : 'in the whole book'}
        </span>
      </Toolbar>
    </Card>
  );
}
